import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppNumberTextField from '@/common/components/form-inputs/AppNumberTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import CloseIcon from '@mui/icons-material/Close';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { SubjectType } from '@/api/enum/common.enum';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
	useCreateQuestionnaireMutation,
	useUpdateQuestionnaireMutation,
} from '@/api/exercise/day21ApiSlice';
import { useParams } from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { FormType } from '@/api/enum/formType.enum';
import { QuestionnaireDTO } from '@/api/DTO/exercise/day21.interface';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

interface Props {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	setCurrentTopTab?: Dispatch<SetStateAction<string>>;
	qaId?: string;
	refetch?: any;
	info?: QuestionnaireDTO;
	hasQuestion?: boolean;
	questionsLength?: number;
}

export default function QuestionnaireForm({
	type,
	info,
	refetch,
	qaId,
	hasQuestion,
	questionsLength,
	setCurrentTopTab,
}: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [createQuestionnaire] = useCreateQuestionnaireMutation();
	const [updateQuestionnaire] = useUpdateQuestionnaireMutation();
	const { day21Id } = useParams();
	const showModal = useModal();
	const typeList = [
		{ id: 0, name: t('day21.radio') },
		{ id: 1, name: t('day21.multiple') },
		{ id: 2, name: t('day21.line') },
		{ id: 3, name: t('day21.answer') },
	];

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		type: Yup.string().required(t('errorMessage.pleaseSelect')),
		title: Yup.string().required(t('errorMessage.required')),
		minValue: Yup.string()
			.nullable()
			.when('type', {
				is: (type: string | number) => type == SubjectType.Linear,
				then: (schema) =>
					schema
						.required(t('errorMessage.required'))
						.test(
							'min-less-than-max',
							t('errorMessage.min<max'),
							function (value) {
								const { parent } = this;
								const maxValue = parent.maxValue;
								return (
									value === null ||
									maxValue === null ||
									Number(value) < Number(maxValue)
								);
							}
						)
						.test('more20', t('errorMessage.more20'), function (value) {
							const { parent } = this;
							const minValue = parent.minValue;
							return (
								Number(value) - Number(minValue) > -20 &&
								Number(value) - Number(minValue) < 20
							);
						}),
			}),
		maxValue: Yup.string()
			.nullable()
			.when('type', {
				is: (type: string | number) => type == SubjectType.Linear,
				then: (schema) =>
					schema
						.required(t('errorMessage.required'))
						.test(
							'max-greater-than-min',
							t('errorMessage.max>min'),
							function (value) {
								const { parent } = this;
								const minValue = parent.minValue;
								return (
									value === null ||
									minValue === null ||
									Number(value) > Number(minValue)
								);
							}
						)
						.test('more20', t('errorMessage.more20'), function (value) {
							const { parent } = this;
							const minValue = parent.minValue;
							return (
								Number(value) - Number(minValue) > -20 &&
								Number(value) - Number(minValue) < 20
							);
						}),
			}),
		minValueTag: Yup.string()
			.nullable()
			.when('type', {
				is: (type: string | number) => type == SubjectType.Linear,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		maxValueTag: Yup.string()
			.nullable()
			.when('type', {
				is: (type: string | number) => type == SubjectType.Linear,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		options: Yup.array().of(
			Yup.object().shape({
				content: Yup.mixed().test(
					'required-if-radio-or-multiple',
					t('errorMessage.required'),
					function (value) {
						const { parent } = this;
						return parent.content;
					}
				),
			})
		),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const {
		fields: optionsFields,
		append: optionsAppend,
		remove: optionsRemove,
		update: optionsUpdate,
	}: any = useFieldArray({
		control: methods.control,
		name: 'options',
	});

	const watchType = methods.watch('type');

	useEffect(() => {
		methods.setValue('options', []);
		if (watchType === SubjectType.Radio || watchType === SubjectType.Multiple) {
			addOptions();
		}
	}, [watchType]);

	useEffect(() => {
		const obj = { ...info };
		if (
			info?.type !== SubjectType.Radio &&
			info?.type !== SubjectType.Multiple
		) {
			delete obj.options;
		}
		methods.reset(obj);
	}, [info, open]);

	const addOptions = () => {
		optionsAppend({
			content: '',
			id: new Date().getTime(),
		});
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		console.log(data);
		const obj: any = {
			title: data.title,
			type: data.type,
			seriesId: day21Id,
		};
		if (
			data.type === String(SubjectType.Radio) ||
			data.type === String(SubjectType.Multiple)
		) {
			obj.options = (data.options || []).map((item: any) => {
				const obj: any = { content: item.content };
				if (typeof item.id === 'string') obj.id = item.id;
				return obj;
			});
		}
		if (data.type === String(SubjectType.Linear)) {
			obj.maxValue = data.maxValue;
			obj.minValue = data.minValue;
			obj.maxValueTag = data.maxValueTag;
			obj.minValueTag = data.minValueTag;
		}

		if (type == FormType.create) {
			const response =
				type == FormType.create
					? await createQuestionnaire(obj)
					: await updateQuestionnaire({ ...obj, id: qaId });
			if ('data' in response) {
				enqueueSnackbar(
					type == FormType.create
						? t('snackbar.createQuestionnaireSuccess')
						: t('snackbar.updateQuestionnaireSuccess'),
					{
						variant: 'success',
						anchorOrigin: { horizontal: 'center', vertical: 'top' },
						autoHideDuration: 3000,
						action: (key) => {
							return <CloseIcon onClick={() => closeSnackbar(key)} />;
						},
					}
				);
				refetch && refetch();
				setCurrentTopTab && setCurrentTopTab('content');
				setOpen(false);
			}
		} else {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={true}
					title={t('day21.fixQuestionnaire')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('day21.isFixQuestionnaire')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.isConfirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						const response = await updateQuestionnaire({ ...obj, id: qaId });
						if ('data' in response) {
							enqueueSnackbar(t('snackbar.updateQuestionnaireSuccess'), {
								variant: 'success',
								anchorOrigin: { horizontal: 'center', vertical: 'top' },
								autoHideDuration: 3000,
								action: (key) => {
									return <CloseIcon onClick={() => closeSnackbar(key)} />;
								},
							});
							refetch && refetch();
							setOpen(false);
						}
					}}
				/>
			));
		}
	};

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};
	const handleFix = () => {
		setOpen(true);
	};
	return (
		<>
			{type == FormType.create && !hasQuestion && (
				<Button
					variant='contained'
					style={{ alignItems: 'center' }}
					endIcon={<NoteAltOutlinedIcon />}
					onClick={() => setOpen(true)}
					sx={{ marginRight: '24px' }}
				>
					{t('day21.createQuestionnaire')}
				</Button>
			)}
			{type == FormType.view && (
				<Button
					color='secondary'
					component='label'
					endIcon={<CreateOutlinedIcon />}
					onClick={() => handleFix()}
				>
					{t('day21.fix')}
				</Button>
			)}

			{questionsLength && (
				<Button
					endIcon={<AddCircleOutlineIcon />}
					color='primary'
					component='label'
					variant='outlined'
					fullWidth
					sx={{ margin: '24px 0' }}
					size='large'
					onClick={() => setOpen(true)}
				>
					{t('day21.createTopic')}
				</Button>
			)}

			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('day21.createQuestionnaire')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						{type == FormType.create && !hasQuestion && (
							<Typography variant='body2'>{t('day21.firstTopic')}</Typography>
						)}

						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={12}>
									<Grid container spacing={3} marginBottom={2.5}>
										<Grid item xs={1}>
											<Typography variant='body1'>
												{t('day21.subject')}
												{type == FormType.create && !hasQuestion && 1}
												{questionsLength &&
													questionsLength > 0 &&
													questionsLength + 1}
												{info?.sort}
											</Typography>
										</Grid>
										<Grid item xs={11}>
											<Grid container spacing={2}>
												<Grid item xs={12}>
													<AppTextField
														control={methods.control}
														name={`title`}
														label={`${t('day21.titleText')}  `}
														maxLength={100}
														showMaxLength
													/>
												</Grid>

												<Grid item xs={12}>
													<AppDropDownMenu
														control={methods.control}
														name={'type'}
														label={t('day21.optionType')}
														options={typeList}
														required
													/>
												</Grid>
												{(watchType === SubjectType.Radio ||
													watchType === SubjectType.Multiple) &&
													optionsFields.map((item: any, index: number) => (
														<Grid item xs={12} key={item.id}>
															<Stack direction={'row'} alignItems={'center'}>
																<AppTextField
																	control={methods.control}
																	name={`options[${index}].content`}
																	label={`${t('day21.option')} ${index + 1}`}
																	maxLength={100}
																	showMaxLength
																/>
																{optionsFields.length > 1 && (
																	<IconButton
																		aria-label='delete'
																		sx={{
																			color: '#D32F2F',
																			marginLeft: '15px',
																		}}
																		onClick={() => {
																			optionsRemove(index, item);
																		}}
																	>
																		<DeleteIcon />
																	</IconButton>
																)}
															</Stack>
														</Grid>
													))}

												{(watchType === SubjectType.Radio ||
													watchType === SubjectType.Multiple) && (
													<Stack
														direction={'row'}
														alignItems={'center'}
														sx={{ width: '100%' }}
													>
														<Button
															component='label'
															sx={{ height: 24, marginLeft: '16px' }}
															endIcon={<AddIcon />}
															onClick={() => addOptions()}
														>
															{t('day21.addOption')}
														</Button>
													</Stack>
												)}

												{watchType === SubjectType.Linear && (
													<>
														<Grid item xs={6}>
															<AppNumberTextField
																control={methods.control}
																name={`minValue`}
																type='number'
																nonNegative={true}
																label={t('day21.minValue')}
															/>
														</Grid>
														<Grid item xs={6}>
															<AppNumberTextField
																control={methods.control}
																name={`maxValue`}
																type='number'
																nonNegative={true}
																label={t('day21.maxValue')}
															/>
														</Grid>
														<Grid item xs={6}>
															<AppTextField
																control={methods.control}
																name={`minValueTag`}
																label={`${t('day21.minValueTag')}  `}
															/>
														</Grid>
														<Grid item xs={6}>
															<AppTextField
																control={methods.control}
																name={`maxValueTag`}
																label={`${t('day21.maxValueTag')}  `}
															/>
														</Grid>
													</>
												)}
											</Grid>
										</Grid>
									</Grid>
									<Divider />
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{type == FormType.create ? t('button.add') : t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
